import { Box, Button, Container } from "@material-ui/core"
import {
  postGiftCardGenerateVerificationCode,
  postGroupPurchaseGetGroupPurchase,
} from "api"
import { Desktop } from "common/MediaQueries"
import GenericButton from "components/GenericButton/GenericButton"
import { GenericInput } from "components/GenericInput/GenericInput"
import { useOtpContext } from "components/OtpStep/OtpContext"
import {
  GroupPurchasesSteps,
  useGroupPurchasesContext,
} from "components/PurchasePage/GroupPurchasesContext"
import LegalCheckboxes from "components/PurchasePage/LegalCheckboxes"
import { usePurchaseContext } from "components/PurchasePage/PurchaseContext"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { useRouter } from "next/router"
import { createRef, useEffect, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import styled from "styled-components"
import GroupPurchasesForm from "./GroupPurchasesForm"
import GroupPurchasesHeader from "./GroupPurchasesHeader"
import GroupPurchasesPageCover from "./GroupPurchasesPageCover"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledWrapper = styled(Container)`
  margin-top: 80px;
  padding-right: 0px;
  padding-left: 0px;
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: row-reverse;
    margin-top: 120px;
    justify-content: space-between;
  }
`

const StyledButtonWrapper = styled(Box)`
  width: 328px;
  margin-bottom: 40px;
  margin-top: 86px;
  position: relative;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 20px;
    position: fixed;
    bottom: 0;
    right: 0;
    width: calc(100% - 32px);
    border-radius: 10px;
    box-shadow: 0 -5px 10px 0 hsla(0, 0%, 0%, 0.12);
    background-color: #fff;
    padding: 12px 16px 10px 16px;
    margin-top: 0px;
    z-index: 999;
    margin-bottom: 0px;
  }
  box-sizing: content-box;
`

const StyledButton = styled(Button).attrs({ variant: "contained" })<{
  $color: string
  $background: string
}>`
  color: ${(p) => p.$color};
  background-color: ${(p) => p.$background};
  height: 48px;
  width: 100%;
  &:hover {
    background-color: ${(p) => p.$background} !important;
    color: ${(p) => p.$color};
  }
  &:disabled {
    background-color: ${(p) => p.theme.colors.disabled} !important;
    color: #757575 !important;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    border-radius: 0px;
  }
`

export const StyledMain = styled(Box)`
  padding-left: 16px;
  padding-right: 16px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding-left: 60px;
    padding-right: 60px;
  }
`

const StyledFormLegend = styled("legend")`
  width: fit-content;
  font-size: 26px;
  font-weight: bold;
  line-height: 32px;
  text-align: right;
  color: #000000;
  margin-top: 30px;
  font-family: Speedee, NarkisBlock, sans-serif;
`

const StyledFieldWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 27px;
  margin-top: 47px;

  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 30px 10px;
    margin-top: 27px;
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    > * {
      width: 100%;
    }
  }
`

const StyledLegalCheckboxesWrapper = styled("div")`
  margin-bottom: 100px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 0px;
  }
`

const LeaderDetailsPage = () => {
  const [pageTitle, setPageTitle] = useState("")

  const reCaptchaRef = createRef<ReCAPTCHA>()

  const router = useRouter()

  const {
    groupPurchasesData,
    setGroupPurchasesData,
    setGroupPurchasesSteps,
    setPrevPageId,
    changed,
    setPrevPath,
  } = useGroupPurchasesContext()

  const {
    setGroupPurchaseGuid,
    groupPurchaseGuid,
    setGroupPurchaseData,
    groupPurchaseData,
  } = usePurchaseContext()
  const { setTotalTimeoutInSeconds, setPhoneNumber } = useOtpContext()

  useEffect(() => {
    // Enables hitting back button while navigating through SPA style page
    setTimeout(() => {
      setPrevPageId(2)
      setPrevPath("/groupPurchases")
    }, 500)
  }, [changed])

  const getCardData = async (groupGuid: string) => {
    try {
      const getGroupPurchase = await postGroupPurchaseGetGroupPurchase({
        groupPurchaseGuid: groupGuid,
      })
      getGroupPurchase && setGroupPurchaseData(getGroupPurchase?.data?.body)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (router?.query?.id) {
      const groupGuid = router?.query?.id?.toString()
      setGroupPurchaseGuid(groupGuid)
      getCardData(groupGuid)
    }
  }, [router])

  useEffect(() => {
    if (groupPurchaseGuid) {
      //@ts-ignore
      if (groupPurchaseData?.isDisabledGroup) {
        setPageTitle("הי, נראה שהקבוצה אינה פעילה יותר")
      } else {
        setPageTitle(
          `היי הוזמנת לאיסוף כסף עבור רכישה קבוצתית ל״${
            //@ts-ignore
            groupPurchaseData?.groupName || ""
          }״`
        )
      }
    } else {
      setPageTitle("זה הזמן למלא את הפרטים שלך כמנהל/ת הקבוצה")
    }
  }, [groupPurchaseGuid, groupPurchaseData])

  // Scroll to top on page load
  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0)
    }
  }, [])

  const groupPurchasesInitialData = {
    firstName: groupPurchasesData?.firstName || "",
    lastName: groupPurchasesData?.lastName || "",
    phoneNumber: groupPurchasesData?.phoneNumber || "",
    email: groupPurchasesData?.email?.trim() || "",
    //@ts-ignore
    checkedOne: groupPurchasesData?.checkedOne || false,
    checkedTwo: groupPurchasesData?.approveMarketingContent || false,
  }

  const formikRef = useRef<any>(null)

  const saveGroupPurchasesData = () => {
    if (formikRef.current) {
      const formValues = formikRef.current?.values
      setGroupPurchasesData({ ...groupPurchasesData, ...formValues })
    }
  }

  useEffect(() => {
    if (router.query && router.query.focus && document) {
      const input = document?.getElementById(router.query.focus.toString())
      input?.scrollIntoView()
      setTimeout(() => {
        input?.focus()
      }, 1500)
    }
  }, [router])

  const getVerificationCode = async (
    phone: string,
    gRecaptchaResponse: string
  ) => {
    try {
      setGroupPurchasesSteps(GroupPurchasesSteps.LOADER)
      router.push("/groupPurchases/otp")
      const response = await postGiftCardGenerateVerificationCode({
        mobilePhone: phone.replace(/-/g, ""),
        toSendSms: true,
        gRecaptchaResponse,
      })
      if (response?.status === 200 && response?.data) {
        setGroupPurchasesSteps(GroupPurchasesSteps.LeaderDetails)
        // leaveBalancePage("phoneNumber")
        setTotalTimeoutInSeconds(response.data.body.totalTimeoutInSeconds)
      }
    } catch (error) {
      setGroupPurchasesSteps(GroupPurchasesSteps.FAIL)
    }
  }

  return (
    <StyledWrapper>
      <GroupPurchasesPageCover backBtnOnClick={saveGroupPurchasesData} />
      <StyledMain>
        <GroupPurchasesHeader
          mainTitle={pageTitle}
          backBtnOnClick={saveGroupPurchasesData}
          component="h2"
        />
        {
          //@ts-ignore
          groupPurchaseGuid && groupPurchaseData?.isDisabledGroup ? (
            <StyledButtonWrapper>
              <StyledButton
                type="button"
                $color={"#000"}
                $background={"#ffb900"}
                onClick={() => router.push("/")}
              >
                לעמוד הבית
              </StyledButton>
            </StyledButtonWrapper>
          ) : (
            <FormikForm
              innerRef={formikRef}
              validationSchema={
                GroupPurchasesForm.schemaGroupPurchasesLeaderDetails
              }
              initialValues={groupPurchasesInitialData}
              onSubmit={async (values) => {
                reCaptchaRef?.current?.reset()
                const recaptchaResponse =
                  await reCaptchaRef?.current?.executeAsync()
                setGroupPurchasesData({
                  ...groupPurchasesData,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  email: values.email?.trim(),
                  phoneNumber: values.phoneNumber,
                  approveMarketingContent: values.checkedTwo,
                })
                setPhoneNumber(values.phoneNumber.replace(/-/g, ""))
                await getVerificationCode(values.phoneNumber, recaptchaResponse)
              }}
              render={(formik) => {
                return (
                  <>
                    <Desktop>
                      <StyledFormLegend>הפרטים שלי</StyledFormLegend>
                    </Desktop>
                    <StyledFieldWrapper>
                      <GenericInput
                        name="firstName"
                        label="שם פרטי (*חובה)"
                        type="text"
                        maxLength={30}
                        topMargin="0"
                        placeholder="שם פרטי (*חובה)"
                      />
                      <GenericInput
                        name="lastName"
                        label="שם משפחה (*חובה)"
                        type="text"
                        maxLength={30}
                        topMargin="0"
                        placeholder="שם משפחה (*חובה)"
                      />
                      <GenericInput
                        name="phoneNumber"
                        label="(*חובה) מספר טלפון"
                        type="tel"
                        maxLength={30}
                        topMargin="0"
                        placeholder="טלפון (*חובה)"
                      />
                      <GenericInput
                        name="email"
                        label="(*חובה) מייל"
                        type="email"
                        maxLength={60}
                        topMargin="0"
                        placeholder="אימייל (*חובה)"
                      />
                    </StyledFieldWrapper>
                    <StyledLegalCheckboxesWrapper>
                      <LegalCheckboxes
                        saveGiftCardData={saveGroupPurchasesData}
                        prevPageId={3}
                      />
                    </StyledLegalCheckboxesWrapper>
                    <StyledButtonWrapper>
                      <GenericButton
                        type="submit"
                        btnType="filled"
                        isdisabled={
                          formik?.isSubmitting ||
                          Object.keys(formik?.errors).length > 0
                        }
                      >
                        המשך
                      </GenericButton>
                    </StyledButtonWrapper>
                    <ReCAPTCHA
                      ref={reCaptchaRef}
                      sitekey={RECAPTCHA_KEY}
                      size="invisible"
                      hl="iw"
                      tabIndex={-1}
                    />
                  </>
                )
              }}
            ></FormikForm>
          )
        }
      </StyledMain>
    </StyledWrapper>
  )
}

export default LeaderDetailsPage
